import { useEffect, useState } from "react";

type HeadingItem = {
  id: string;
  title: string;
};

type Props = {
  headings: HeadingItem[];
};

export default function BlogScrollNav({ headings }: Props) {
  const [activeId, setActiveId] = useState(headings[0]?.id || "");

  useEffect(() => {
    if (!headings.length) return;

    const onScroll = () => {
      const offset = 160;
      let current = headings[0]?.id || "";

      for (let i = 0; i < headings.length; i += 1) {
        const el = document.getElementById(headings[i].id);
        if (!el) continue;
        const rect = el.getBoundingClientRect();
        const next = headings[i + 1]
          ? document.getElementById(headings[i + 1].id)
          : null;
        const nextTop = next ? next.getBoundingClientRect().top : Infinity;

        if (rect.top <= offset && nextTop > offset) {
          current = headings[i].id;
          break;
        }
      }

      setActiveId(current);
    };

    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [headings]);

  if (!headings.length) return null;

  // Figma track is 205px tall with marks centered; grow with section count.
  const marksBlockHeight = Math.max((headings.length - 1) * 8, 0);
  const trackHeight = Math.max(205, marksBlockHeight + 120);

  const scrollToHeading = (id: string) => {
    const el = document.getElementById(id);
    if (!el) return;
    const top = el.getBoundingClientRect().top + window.scrollY - 80;
    window.scrollTo({ top, behavior: "smooth" });
    setActiveId(id);
  };

  return (
    <nav
      className="blog-scroll-nav"
      aria-label="Article sections"
      style={{ height: trackHeight }}
    >
      <div className="blog-scroll-nav-panel" aria-hidden="true">
        <p className="blog-scroll-nav-title">What&apos;s in this article</p>
        <ul className="blog-scroll-nav-list">
          {headings.map((item) => (
            <li key={item.id}>
              <a
                href={`#${item.id}`}
                className={`blog-scroll-nav-item${
                  activeId === item.id ? " active" : ""
                }`}
                tabIndex={-1}
                onClick={(e) => {
                  e.preventDefault();
                  scrollToHeading(item.id);
                }}
              >
                {item.title}
              </a>
            </li>
          ))}
        </ul>
      </div>

      <div className="blog-scroll-nav-rail">
        <svg
          className="blog-scroll-nav-track"
          width="30"
          height={trackHeight}
          viewBox={`0 0 30 ${trackHeight}`}
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden="true"
          preserveAspectRatio="none"
        >
          {/* Exact Figma path, stretched vertically when taller than 205 */}
          <path
            d={`M0 0L24.1421 24.1421C27.8929 27.8929 30 32.9799 30 38.2843V${
              trackHeight - 38.2843
            }C30 ${trackHeight - 32.9799} 27.8929 ${trackHeight - 27.8929} 24.1421 ${
              trackHeight - 24.1421
            }L0 ${trackHeight}V0Z`}
            fill="#EFEFF9"
          />
        </svg>

        <ul className="blog-scroll-nav-marks">
          {headings.map((item) => (
            <li key={item.id}>
              <a
                href={`#${item.id}`}
                className={`blog-scroll-mark${
                  activeId === item.id ? " active" : ""
                }`}
                aria-label={item.title}
                title={item.title}
                onClick={(e) => {
                  e.preventDefault();
                  scrollToHeading(item.id);
                }}
              />
            </li>
          ))}
        </ul>
      </div>
    </nav>
  );
}
